import styles from './cancel-visit-page.module.css';
import { Button } from '@/components/button';
import { Banner } from '@/components/banner';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form } from '@/components/form';
import { useTranslation } from '@/contexts/i18n-context';
import { RadioGroup } from '@/components/inputs/radio-group';
import {
  CancelEncounterErrorResponse,
  CancelEncounterErrorType,
  CancellationReason,
} from '@/api/api.schemas';
import { useNavigate } from '@/util/react-router-dom';
import { useCreateEncountersCancel } from '@/api/cancel-encounter';
import { useGetCancellationReasons } from '@/api/get-cancellation-reasons';
import { TextInput } from '@/components/inputs/text-input';
import { useEncounter } from '@/contexts/encounter-context';
import { useBranding } from '@/outlets/branding-outlet';
import { zt } from '@/util/validators';
import { Section } from '@/components/containers/section';
import { PageContainer } from '@/components/containers/page-container';
import { Loader } from '@/components/loaders/loader';

const translationBase = 'visit.cancel';

const cancelVisitSchema = z.object({
  cancelReason: z.nativeEnum(CancellationReason, {
    message: zt('common.errors.required'),
  }),
  cancelFeedback: z.string().optional(),
});

type CancelVisitFormData = z.infer<typeof cancelVisitSchema>;

export function CancelVisitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { encounter } = useEncounter();
  const { networkId } = useBranding();

  const form = useForm<CancelVisitFormData>({
    resolver: zodResolver(cancelVisitSchema),
    mode: 'onBlur',
  });

  const { mutate: cancelEncounter, isPending, error } = useCreateEncountersCancel();
  const { data: cancellationReasons, isLoading: isLoadingCancellationReasons } =
    useGetCancellationReasons({ networkId });

  const getErrorMessage = (error: CancelEncounterErrorResponse) => {
    if (error.type === CancelEncounterErrorType.NotFound) {
      return t(`${translationBase}.errors.notFound`);
    }
    return t('common.errors.unknown');
  };

  const onSubmit: SubmitHandler<CancelVisitFormData> = async (data) => {
    cancelEncounter(
      { encounterId: encounter.id, data },
      {
        onSuccess: () => navigate('/'),
      },
    );
  };

  if (isLoadingCancellationReasons) {
    return <Loader type="large" />;
  }

  return (
    <PageContainer className={styles.pageContainer}>
      <Section className={styles.container} isFormSection={false}>
        <div className={styles.titleContainer}>
          <h1 className="h3">{t(`${translationBase}.title`)}</h1>
          <p>{t(`${translationBase}.description`)}</p>
        </div>
        <Form className={styles.form} form={form} onSubmit={onSubmit}>
          {!!error && <Banner type="error">{getErrorMessage(error)}</Banner>}
          <RadioGroup
            label={t(`${translationBase}.cancelReason.label`)}
            name="cancelReason"
            options={
              cancellationReasons?.map((reason) => ({
                value: reason.id,
                label: reason.description,
              })) ?? []
            }
            className={styles.cancelReason}
          />
          <TextInput
            aria-label={t(`${translationBase}.cancelFeedbackLabel`)}
            placeholder={t(`${translationBase}.cancelFeedback`)}
            name="cancelFeedback"
            withIconsIndicators={false}
          />
          <div className={styles.buttonContainer}>
            <Button variant="outlined" as="a" to="/" disabled={isPending}>
              {t(`${translationBase}.cancel`)}
            </Button>
            <Button type="submit" variant="filled" loading={isPending}>
              {t(`${translationBase}.confirm`)}
            </Button>
          </div>
        </Form>
      </Section>
    </PageContainer>
  );
}
